'use client'
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { gsap } from 'gsap'
import { useLenis } from 'lenis/react'
import Typography from 'src/components/design-system/Typography'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import ChevronSelect from 'public/icons/interface/select.svg'
import Arrow from 'public/icons/arrows/arrow-right.svg'

const AsideMenu = ({ sections = [], routeContext = 'main' }) => {
  const translate = useTranslateEntry()
  const lenis = useLenis()
  const isDomainJournal = routeContext === 'journal-domain'

  const [activeSection, setActiveSection] = useState(null)
  const [isVisible, setIsVisible] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [availableSections, setAvailableSections] = useState([])

  const navRef = useRef(null)
  const menuListRef = useRef(null)
  const dropdownRef = useRef(null)
  const lastScrollY = useRef(0)

  useEffect(() => {
    const checkSections = () => {
      const available = sections.filter((section) => {
        const element = document.getElementById(section?.id)
        return element !== null
      })
      setAvailableSections(available)

      if (available.length > 0 && !activeSection) {
        setActiveSection(available[0].id)
      }
    }

    checkSections()
    const timeout = setTimeout(checkSections, 500)

    return () => clearTimeout(timeout)
  }, [sections, activeSection])

  useEffect(() => {
    if (availableSections.length === 0) return

    let ticking = false

    const updateActiveSection = () => {
      const scrollTop = window.scrollY + 400
      let currentActive = availableSections[0]?.id
      for (let i = availableSections.length - 1; i >= 0; i--) {
        const section = availableSections[i]
        const element = document.getElementById(section.id)

        if (element) {
          const rect = element.getBoundingClientRect()
          const elementTop = window.scrollY + rect.top

          if (elementTop <= scrollTop) {
            currentActive = section.id
            break
          }
        }
      }

      setActiveSection((prev) => (prev !== currentActive ? currentActive : prev))
      ticking = false
    }

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateActiveSection)
        ticking = true
      }
    }

    updateActiveSection()

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [availableSections])

  useEffect(() => {
    const scrollThreshold = 100

    const handleScroll = () => {
      const currentScrollY = window.scrollY
      const isMobile = window.innerWidth < 1440

      if (!isMobile) {
        setIsVisible(false)
        setIsDropdownOpen(false)
        return
      }

      if (currentScrollY > scrollThreshold) {
        setIsVisible(true)
      } else {
        setIsVisible(false)
        setIsDropdownOpen(false)
      }

      lastScrollY.current = currentScrollY
    }

    const handleResize = () => {
      const isMobile = window.innerWidth < 1440
      if (!isMobile) {
        setIsVisible(false)
        setIsDropdownOpen(false)
      } else {
        handleScroll()
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleResize)

    handleScroll()

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    if (!menuListRef.current || !activeSection) return

    const activeItem = menuListRef.current.querySelector(`[data-section="${activeSection}"]`)
    if (!activeItem) return

    const menuRect = menuListRef.current.getBoundingClientRect()
    const itemRect = activeItem.getBoundingClientRect()

    if (itemRect.top < menuRect.top || itemRect.bottom > menuRect.bottom) {
      activeItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [activeSection])

  useEffect(() => {
    if (!dropdownRef.current) return

    if (isDropdownOpen) {
      gsap.fromTo(
        dropdownRef.current,
        { opacity: 0, y: -6, display: 'none' },
        { opacity: 1, y: 0, display: 'block', duration: 0.2, ease: 'power2.out' },
      )
    } else {
      gsap.to(dropdownRef.current, { opacity: 0, y: -6, display: 'none', duration: 0.2 })
    }
  }, [isDropdownOpen])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setIsDropdownOpen(false)
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isDropdownOpen])

  const scrollToSection = useCallback(
    (sectionId) => {
      const element = document.getElementById(sectionId)
      if (!element) return

      if (lenis) {
        lenis.scrollTo(element, { offset: -100 })
      } else {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }

      setIsDropdownOpen(false)
    },
    [lenis],
  )

  const getActiveSectionTitle = () => {
    const section = availableSections.find((s) => s.id === activeSection)
    return section ? section.title : translate('T_NAVIGATION')
  }

  if (availableSections.length === 0) return null

  return (
    <>
      {/* Desktop */}
      <nav
        ref={navRef}
        className="hidden xl:block sticky top-176 w-full"
        aria-label={translate('T_ARTICLE_NAVIGATION')}
      >
        <div
          className={`${isDomainJournal ? 'bg-basic-gray10' : 'bg-dark-blue-dark-blue6'} flex flex-col gap-y-24 p-12`}
        >
          <Typography
            variant="h3-caps-bold"
            component="h2"
            color={isDomainJournal ? 'text-basic-black' : 'text-dark-blue-dark-blue1'}
          >
            {translate('T_ON_THIS_PAGE')}
          </Typography>

          <ul
            ref={menuListRef}
            className="flex flex-col max-h-[min(588px,70vh)] overflow-y-auto scrollbar-thin scrollbar-thumb-dark-blue-dark-blue5 scrollbar-track-transparent"
            data-lenis-prevent
          >
            {availableSections.map((section) => {
              const isActive = activeSection === section.id
              return (
                <li key={section.id} data-section={section.id}>
                  <button
                    onClick={() => scrollToSection(section.id)}
                    className={`
                      w-full text-left py-12 transition-all duration-200 border-b flex gap-x-8 items-center
                      ${
                        isActive
                          ? isDomainJournal
                            ? 'border-basic-gray7'
                            : 'border-dark-blue-dark-blue3'
                          : isDomainJournal
                            ? 'border-basic-gray7 hover:border-basic-black'
                            : 'border-dark-blue-dark-blue5 hover:border-dark-blue-dark-blue3'
                      }
                    `}
                    aria-current={isActive ? 'true' : undefined}
                  >
                    {isActive && (
                      <span className="w-18 h-18 flex items-center justify-center rounded-full border border-basic-black">
                        <Arrow className="text-basic-black rotate-180" />
                      </span>
                    )}
                    <Typography
                      variant={isActive ? 'h3-bold' : 'h3-light'}
                      component="span"
                      color={isDomainJournal ? 'text-basic-black' : 'text-basic-black'}
                    >
                      {section.title}
                    </Typography>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      </nav>

      {/* Mobile */}
      <div
        className={`
          xl:hidden fixed top-155 left-0 z-50 w-full
          transition-all duration-300
          ${isVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 pointer-events-none'}
        `}
      >
        <nav ref={navRef} aria-label={translate('T_ARTICLE_NAVIGATION')} className="relative">
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            aria-controls="asideMenuDropdown"
            aria-expanded={isDropdownOpen}
            className={`flex h-44 w-full items-center justify-between border-b px-16 text-basic-black ${
              isDomainJournal
                ? 'border-b-basic-gray7 bg-basic-gray10'
                : 'border-b-dark-blue-dark-blue3 bg-dark-blue-dark-blue6'
            }`}
          >
            <Typography
              variant="h3-light"
              component="span"
              color={isDomainJournal ? 'text-basic-black' : 'text-basic-black'}
            >
              {getActiveSectionTitle()}
            </Typography>
            <ChevronSelect
              className={`text-basic-black transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          </button>

          {isDropdownOpen && (
            <ul
              id="asideMenuDropdown"
              ref={dropdownRef}
              role="listbox"
              className={`absolute left-0 top-full z-10 max-h-[500px] w-full overflow-y-auto border opacity-0 ${
                isDomainJournal
                  ? 'border-basic-gray7 bg-basic-gray10'
                  : 'border-blueish-blueish2 bg-dark-blue-dark-blue6'
              }`}
              data-lenis-prevent
            >
              {availableSections.map((section) => {
                const isActive = activeSection === section.id
                return (
                  <li
                    key={section.id}
                    role="option"
                    aria-selected={isActive}
                    tabIndex={0}
                    onClick={() => scrollToSection(section.id)}
                    className={`cursor-pointer px-16 py-14 ${
                      isDomainJournal
                        ? `hover:bg-basic-gray8 hover:text-basic-black ${isActive ? 'bg-basic-gray8 text-basic-black' : ''}`
                        : `hover:bg-blueish-blueish2 hover:text-basic-white ${isActive ? 'bg-blueish-blueish2 text-basic-white' : ''}`
                    }`}
                  >
                    <Typography
                      variant="h3-light"
                      component="span"
                      color={isDomainJournal ? 'text-basic-black' : isActive ? 'text-basic-white' : 'text-basic-black'}
                    >
                      {section.title}
                    </Typography>
                  </li>
                )
              })}
            </ul>
          )}
        </nav>
      </div>
    </>
  )
}

export default AsideMenu
